import React, { useEffect, useState } from 'react'   
import { Input, Pagination, Button, Tabs, TabItem } from '@/components/atoms/index'
import { vars } from '@/style/theme.css'
import { Icon } from '@/icons'
import { useNavigate, useLocation } from 'react-router-dom'
import { getSlugPrefix } from '@/utils/slug'
import WordTable from '@/components/organisms/Table/AcademyWordTable'
import { academyWordApi } from '@/services/api'
import { useSessionSlug } from '@/hooks/useSessionSlug'

interface AcademyWordRow {
    id: string
    wordCode: string
    word: string
    meaning: string
    ttsType: string
    registrant: string
    createdAt: string
    uploadStatus: 'completed' | 'failed'
}


// 서버 데이터 상태
const initialRows: AcademyWordRow[] = []

// 탭 아이템 컴포넌트
export const AcademyWordTabItems = ({ activeTab, setActiveTab }: { 
    activeTab: 'word' | 'wordbook' | 'exam'; 
    setActiveTab: (tab: 'word' | 'wordbook' | 'exam') => void;     
}) => {
    const navigate = useNavigate();
    const tabItems: TabItem[] = [
        { id: 'word', label: '단어 관리', onClick: () => navigate('/academy/word/list') },
        { id: 'wordbook', label: '단어장 관리', onClick: () => navigate('/academy/word/word_book/list') },
        { id: 'exam', label: '단어 시험지 등록', onClick: () => navigate('/academy/word/word_exam/list') },
    ];
    return <Tabs tabs={tabItems} activeId={activeTab} />;
};



export default function AcademyWordList() {
    const [searchText, setSearchText] = useState('');
    const [rows, setRows] = useState<AcademyWordRow[]>(initialRows);
    const [pageTotal, setPageTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    
    const navigate = useNavigate();
    const location = useLocation();
    const slugPrefix = getSlugPrefix(location.pathname);

    const fetchList = async (p = page, overrideSearch?: string) => {
        try {
            const qVal = typeof overrideSearch === 'string' ? overrideSearch : searchText;
            const res = await academyWordApi.list({ q: qVal, page: p - 1, size: pageSize });
            const pageData = res?.data || res; // ApiResponse 래핑 대응
            const content = pageData?.content || pageData?.page?.content || [];
            const total = pageData?.totalElements || pageData?.page?.totalElements || content.length;
            const toGenderKo = (v: string) => (v === 'male' ? '남성' : v === 'female' ? '여성' : (v || ''));
            const toAgeKo = (v: string) => (
                v === 'child' ? '아동' : v === 'young' ? '청소년' : v === 'adult' ? '성인' : v === 'elder' ? '노년' : (v || '')
            );
            const toSpeedKo = (v: string) => (v === 'slow' ? '느림' : v === 'normal' ? '보통' : v === 'fast' ? '빠름' : (v || ''));
            const toPitchKo = (v: string) => (v === 'low' ? '낮음' : v === 'medium' ? '보통' : v === 'high' ? '높음' : (v || ''));

            const mapped: AcademyWordRow[] = content.map((item: any) => {
                const ttsKorean = `${toGenderKo(item.gender)}/${toAgeKo(item.age)}/${toSpeedKo(item.speed)}/${toPitchKo(item.pitch)}`;
                return {
                    id: String(item.id),
                    wordCode: item.code || '',
                    word: item.word,
                    meaning: item.meaning,
                    ttsType: ttsKorean,
                    registrant: item.registrant || '',
                    createdAt: (item.createdAt || '').toString().replace('T', ' ').substring(0, 16),
                    uploadStatus: 'completed',
                };
            });
            setRows(mapped);
            setPage(p);
            setPageTotal(total);
        } catch (e) {
            console.error(e);
        }
    };

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const pParam = Number(params.get('page') || '1');
        const sParam = Number(params.get('size') || '10');
        const qParam = params.get('q') || '';
        if (pageSize !== sParam) setPageSize(sParam);
        if (searchText !== qParam) setSearchText(qParam);
        fetchList(pParam, qParam);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.search]);

    return (
        <main className='flex flex-col flex-1 pt-8'>
            <div className='flex flex-row justify-between items-start'>
                <h1 className='heading1'>단어 관리</h1>
                <Button intent="primary" onClick={() => navigate(`${slugPrefix}/academy/word/detail`)}>
                    단어 등록하기
                </Button>
            </div>

            <AcademyWordTabItems activeTab='word' setActiveTab={() => {}} />
            
            <main className='flex flex-col w-full'>
                <section className='flex flex-col gap-2 items-start pt-3 w-full lg:flex-row lg:justify-between lg:items-center'>
                    <Input
                        placeholder='검색어를 입력해주세요.'
                        className='max-w-[500px]'
                        icon={<Icon name="search" size={20} color={vars.color.gray[300]} />}
                        value={searchText}
                        onChange={(e: any) => setSearchText(e?.target?.value || '')}
                        onKeyDown={(e: any) => { if (e.key === 'Enter') { 
                            navigate(`${slugPrefix}/academy/word/list?page=1&size=${pageSize}&q=${encodeURIComponent(searchText)}`);
                        }}}
                    />
                
                </section>

                <section className='flex flex-col flex-1 justify-between pt-5 w-full'>
                <WordTable
                    data={rows}
                    type="list"
                    noResolver={(index) => (pageTotal - ((page - 1) * pageSize + index))}
                />
                </section>
                <div className='py-5'>
                    <Pagination
                        total={pageTotal}
                        pageSize={pageSize}
                        current={page}
                        onChange={(p)=>{ 
                            navigate(`${slugPrefix}/academy/word/list?page=${p}&size=${pageSize}&q=${encodeURIComponent(searchText)}`);
                        }}
                    />
                </div>
            </main>
        </main>
    )
}